import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const TEAM = [
  {
    name: "Luke Browne",
    quals: "BA(Hons) PGCE(M)",
    role: "Director",
    creds: ["QuickBooks Certified ProAdvisor", "Xero Advisor Certified"],
    img: "/luke.webp",
  },
  {
    name: "Abbie Browne",
    quals: "FMAAT",
    role: "Director",
    creds: ["QuickBooks Advanced ProAdvisor", "Xero Advisor Certified"],
    img: "/abbie.webp",
  },
  {
    name: "Mica North",
    quals: "AATQB",
    role: "Accounts & Tax Assistant",
    creds: ["Xero Advisor Certified", "Xero Payroll Advisor"],
    img: "/team-mica.jpg",
  },
  {
    name: "Tom Warne",
    quals: "AATQB",
    role: "Accounts Assistant",
    creds: ["QuickBooks Advanced ProAdvisor", "Xero Advisor Certified"],
    img: "/team-tom.jpg",
  },
  {
    name: "David Akingbehin",
    quals: "",
    role: "Accounts Assistant",
    creds: ["Xero Advisor Certified"],
    img: "/team-david.jpg",
  },
  {
    name: "Lorna Browne",
    quals: "",
    role: "Accounts Administrator",
    creds: [],
    img: "/team-lorna.jpg",
  },
  {
    name: "Vicky Port",
    quals: "MICB PM.dip",
    role: "Accounts & Tax Subcontractor",
    creds: ["QuickBooks Certified ProAdvisor", "Xero Advisor Certified"],
    img: "/team-vicky.jpg",
    link: "https://www.mvpbookkeeping.co.uk/",
  },
];

export const TeamSection = () => (
  <section data-testid="team-section" className="pb-24 sm:pb-36 pt-12 sm:pt-20">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">And the rest of the crew</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14 sm:mb-20">
          <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-2xl">
            The people behind your numbers.
          </h2>
          <p className="text-base text-[#0F172A]/65 max-w-md leading-relaxed">
            More than just accountants — your trusted financial guides, combining
            traditional principles with modern know-how.
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {TEAM.map((m, i) => (
          <motion.article
            key={m.name}
            data-testid={`team-card-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="group rounded-2xl bg-white border border-[#103080]/10 overflow-hidden ambient-shadow"
          >
            <div className="overflow-hidden">
              <img
                src={m.img}
                alt={`${m.name}, ${m.role} at Rosevale Accountants`}
                className="w-full aspect-square object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-5 sm:p-6">
              <h3 className="font-serif font-semibold text-[#103080] text-lg sm:text-xl leading-snug">
                {m.name}
                {m.quals && <span className="text-[#0090D0] text-xs sm:text-sm font-sans font-medium ml-2">{m.quals}</span>}
              </h3>
              <p className="text-xs uppercase tracking-[0.18em] text-[#0F172A]/55 mt-1.5 mb-3">{m.role}</p>
              {m.creds.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {m.creds.map((c) => (
                    <span key={c} className="text-[10px] sm:text-[11px] font-medium text-[#103080] bg-[#40C0E0]/15 rounded-full px-2.5 py-1">
                      {c}
                    </span>
                  ))}
                </div>
              )}
              {m.link && (
                <a
                  href={m.link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="team-vicky-link"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#0090D0] hover:text-[#103080] transition-colors duration-300"
                >
                  mvpbookkeeping.co.uk <ArrowUpRight size={12} />
                </a>
              )}
            </div>
          </motion.article>
        ))}

        <motion.a
          href="https://calendly.com/abbie-browne"
          target="_blank"
          rel="noreferrer"
          data-testid="team-join-card"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
          className="group rounded-2xl bg-[#103080] overflow-hidden ambient-shadow flex flex-col items-center justify-center text-center p-8 min-h-[280px]"
        >
          <p className="font-serif font-semibold text-white text-2xl sm:text-3xl leading-tight mb-3">
            Fancy a chat with us?
          </p>
          <p className="text-white/70 text-sm mb-6">Book a free discovery call — no strings, no hidden fees.</p>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#40C0E0] text-[#103080] font-semibold text-sm px-6 py-3 group-hover:bg-white transition-colors duration-300">
            Book a call <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </motion.a>
      </div>
    </div>
  </section>
);
